import { Fragment, useCallback, useEffect, useState } from 'react';
import { api, ApiError, formatRelative, type ResourceGroup } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Empty, Field, Input, Modal, Select, Spinner } from '../components/ui';
import { DeviceHistory } from '../components/DeviceHistory';

/**
 * Devices: apps running somewhere else - a Pi at a venue, a studio PC - that
 * have dialled in to the hub. The hub knows nothing about what an app does:
 * it lists who is connected and the health each one reports, and Open shows
 * the device's own page through the hub.
 *
 * A device that has connected but not been accepted is a request. Accepting
 * one needs the claim code the device itself is showing, so the person
 * accepting it is the person looking at it.
 */

interface Status {
  health: 'ok' | 'warn' | 'error';
  summary: string;
  fields: { label: string; value: string }[];
}

interface Device {
  id: string;
  clientId: string;
  name: string;
  state: 'pending' | 'adopted' | 'blocked';
  app: string;
  appVersion: string;
  platform: string;
  groupId: string | null;
  online: boolean;
  status: Status | null;
  lastSeenAt: string | null;
  lastIp: string | null;
  adoptedAt: string | null;
  adoptedBy?: string | null;
  firstSeenAt: string;
  /** What the device takes from the hub (update, restart, check); empty when offline. */
  commands: string[];
  lastCommand: { action: string; by: string; at: string; state: 'sent' | 'done' | 'failed'; message: string } | null;
}

type Command = 'update' | 'restart' | 'check' | 'sensors-on' | 'sensors-off';
const COMMAND_LABEL: Record<Command, string> = {
  check: 'Check for updates', update: 'Update now', restart: 'Restart client',
  'sensors-on': 'Turn hardware sensors on', 'sensors-off': 'Turn hardware sensors off',
};

/** What the device last reported about its hardware sensors (a status field): on, off or blocked. */
const sensorsState = (d: Device) => d.status?.fields.find((f) => f.label === 'Hardware sensors')?.value ?? '';

interface Payload {
  devices: Device[];
  requests: Device[];
  blocked: Device[];
  openAvailable: boolean;
}

const HEALTH: Record<Status['health'], { label: string; colour: string }> = {
  ok: { label: 'OK', colour: 'var(--color-good)' },
  warn: { label: 'Warning', colour: 'var(--color-warning)' },
  error: { label: 'Error', colour: 'var(--color-critical)' },
};

function OnlineDot({ online }: { online: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-secondary">
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ background: online ? 'var(--color-good)' : 'var(--color-ink-muted)' }}
      />
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

function HealthBadge({ status, online }: { status: Status | null; online: boolean }) {
  if (!status || !online) return <span className="text-xs text-ink-muted">—</span>;
  const h = HEALTH[status.health] ?? HEALTH.ok;
  return (
    <span
      className="rounded px-1.5 py-0.5 text-[11px] font-medium"
      style={{ color: h.colour, background: `color-mix(in srgb, ${h.colour} 14%, transparent)` }}
    >
      {h.label}
    </span>
  );
}

const appLabel = (d: Device) => [d.app || 'Unknown app', d.appVersion].filter(Boolean).join(' ');

/** What an app is called on the page; an app the hub has not met before is shown by its own id. */
const APP_NAMES: Record<string, string> = {
  'atem-sync': 'ATEM Sync',
  'pi-terminal': 'Pi Terminal',
  'engage-client': 'Engage Live Client',
  'pi-panel': 'Engage Live Panel',
};
const appName = (app: string) => APP_NAMES[app] ?? (app || 'Other apps');

/** Adopted devices, one group per app: a card each, so a fleet of one kind reads as a list. Known apps first. */
function byApp(devices: Device[]): [string, Device[]][] {
  const groups = new Map<string, Device[]>();
  for (const d of devices) groups.set(d.app, [...(groups.get(d.app) ?? []), d]);
  const rank = (app: string) => (app in APP_NAMES ? `0${APP_NAMES[app]}` : `1${app}`);
  return [...groups.entries()].sort(([a], [b]) => rank(a).localeCompare(rank(b)));
}

export function Devices() {
  const { can, user } = useAuth();
  const [data, setData] = useState<Payload | null>(null);
  const [groups, setGroups] = useState<ResourceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  /** Adopt dialog: `true` to find a device by its code, or the request being accepted. */
  const [adopting, setAdopting] = useState<Device | true | null>(null);
  const [editing, setEditing] = useState<Device | null>(null);
  const [removing, setRemoving] = useState<Device | null>(null);
  const [declining, setDeclining] = useState<Device | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await api.get<Payload>('/devices'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load devices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    api.get<{ groups: ResourceGroup[] }>('/admin/groups').then((g) => setGroups(g.groups)).catch(() => setGroups([]));
    // Devices report every 15 seconds; nothing here calls AWS.
    const timer = setInterval(() => void load(), 5000);
    return () => clearInterval(timer);
  }, [load]);

  async function act(key: string, call: () => Promise<unknown>, failed: string): Promise<void> {
    setBusy(key);
    setError(null);
    try {
      await call();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : failed);
    } finally {
      setBusy(null);
      setRemoving(null);
      setDeclining(null);
    }
  }

  async function command(d: Device, action: Command): Promise<void> {
    await act(`${d.id}:${action}`, () => api.post(`/devices/${encodeURIComponent(d.id)}/command`, { action }), `Could not send ${action} to ${d.name}.`);
  }

  /** Every connected device in the list that takes this command; one request each. */
  async function commandAll(list: Device[], action: Command): Promise<void> {
    const targets = list.filter((d) => d.commands.includes(action));
    await act(`all:${action}`, async () => {
      const results = await Promise.allSettled(
        targets.map((d) => api.post(`/devices/${encodeURIComponent(d.id)}/command`, { action })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) throw new ApiError(0, "partial", `${failed} of ${targets.length} did not take it.`);
    }, `Could not send ${action}.`);
  }

  async function open(d: Device): Promise<void> {
    // Opened before the request so the browser counts it as the click's own
    // window, not a pop-up.
    const win = window.open('', '_blank');
    try {
      const r = await api.post<{ url: string }>(`/devices/${encodeURIComponent(d.id)}/open`);
      if (win) win.location.href = r.url;
      else window.location.href = r.url;
    } catch (err) {
      win?.close();
      setError(err instanceof ApiError ? err.message : 'Could not open the device.');
    }
  }

  if (loading) return <Spinner label="Loading devices…" />;

  const devices = data?.devices ?? [];
  const requests = data?.requests ?? [];
  const blocked = data?.blocked ?? [];
  const groupName = (id: string | null) => groups.find((g) => g.id === id)?.name ?? (id ? id : 'No group');
  const path = (d: Device) => `/devices/${encodeURIComponent(d.id)}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Devices</h1>
          <p className="text-sm text-ink-muted">
            Apps running somewhere else that have connected to the hub. Open shows a device's own page, as if you were
            on its network.
          </p>
        </div>
        {can('device:adopt') && (
          <Button variant="primary" onClick={() => setAdopting(true)}>Adopt with a code</Button>
        )}
      </div>

      {error && <Alert tone="critical">{error}</Alert>}
      {data && !data.openAvailable && (
        <Alert tone="warning" title="Open is not set up on this hub">
          Devices can connect and be adopted, but their pages have no address yet (DEVICES_DOMAIN).
        </Alert>
      )}

      {requests.length > 0 && (
        <Card title={`Asking to join (${requests.length})`}>
          <div className="divide-y divide-grid">
            {requests.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-sm">
                <div className="min-w-0">
                  <span className="font-medium">{d.name}</span>
                  <span className="ml-2 text-xs text-ink-muted">
                    {d.clientId} · {appLabel(d)}{d.platform && ` · ${d.platform}`}{d.lastIp && ` · from ${d.lastIp}`}
                  </span>
                  <div className="text-[11px] text-ink-muted">
                    First seen {formatRelative(d.firstSeenAt)}
                    {d.status?.summary && ` · ${d.status.summary}`}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <OnlineDot online={d.online} />
                  <Button variant="primary" onClick={() => setAdopting(d)}>Accept</Button>
                  <Button loading={busy === d.id} onClick={() => setDeclining(d)}>Decline</Button>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-grid px-4 py-2 text-xs text-ink-muted">
            Anything that knows this hub's address can ask. Accept only a device you can see: it shows a claim code,
            and accepting asks for it. A request nobody accepts goes away a week after the device was last seen.
          </div>
        </Card>
      )}

      {devices.length === 0 ? (
        <Card>
          <Empty title="No devices yet">
            Point an app at this hub and it appears here asking to join.
          </Empty>
        </Card>
      ) : (
        byApp(devices).map(([app, list]) => (
          <Card
            key={app}
            title={`${appName(app)} (${list.length})`}
            action={can('device:manage') && list.some((d) => d.commands.includes('update')) ? (
              <Button
                loading={busy === 'all:update'}
                title="Each connected device fetches the newest release from the hub and restarts into it"
                onClick={() => void commandAll(list, 'update')}
              >
                Update all ({list.filter((d) => d.commands.includes('update')).length} connected)
              </Button>
            ) : undefined}
          >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grid text-left text-xs text-ink-muted">
                  <th className="px-4 py-2 font-medium">Device</th>
                  <th className="px-4 py-2 font-medium">App</th>
                  <th className="px-4 py-2 font-medium">Health</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Last seen</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <Fragment key={d.id}>
                    <tr className="border-b border-grid last:border-0 hover:bg-raised/40">
                      <td className="px-4 py-2.5">
                        <button
                          className="text-left font-medium hover:underline"
                          onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                          title="Show what the device reports about itself"
                        >
                          {d.name}
                        </button>
                        <div className="mt-0.5 flex items-center gap-2">
                          <OnlineDot online={d.online} />
                          <span className="text-[11px] text-ink-muted">{groupName(d.groupId)}</span>
                          <span className="font-mono text-[11px] text-ink-muted" title="Client ID: the same on the machine and in its logs">{d.clientId}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-ink-secondary">
                        {appLabel(d)}
                        {d.platform && <div className="text-[11px] text-ink-muted">{d.platform}</div>}
                      </td>
                      <td className="px-4 py-2.5"><HealthBadge status={d.status} online={d.online} /></td>
                      <td className="max-w-[28rem] truncate px-4 py-2.5 text-xs text-ink-secondary">
                        {d.online ? d.status?.summary || '—' : 'Not connected'}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-ink-secondary">
                        {d.online ? 'now' : formatRelative(d.lastSeenAt)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex justify-end gap-1">
                          {can('device:open') && (
                            <Button
                              variant="primary"
                              disabled={!d.online || !data?.openAvailable}
                              title={d.online ? "Open the device's own page" : 'The device is not connected'}
                              onClick={() => void open(d)}
                            >
                              Open
                            </Button>
                          )}
                          <Button variant="ghost" onClick={() => setExpanded(expanded === d.id ? null : d.id)}>
                            {expanded === d.id ? 'Hide' : 'Details'}
                          </Button>
                          {can('device:manage') && (
                            <>
                              <Button variant="ghost" onClick={() => setEditing(d)}>Edit</Button>
                              <Button variant="ghost" onClick={() => setRemoving(d)}>Remove</Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === d.id && (
                      <tr className="border-b border-grid bg-raised/30">
                        <td colSpan={6} className="px-4 py-3">
                          <Details device={d} />
                          {can('device:manage') && (d.commands.length > 0 || d.lastCommand) && (
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                              {(['check', 'update', 'restart'] as Command[]).filter((c) => d.commands.includes(c)).map((c) => (
                                <Button key={c} loading={busy === `${d.id}:${c}`} onClick={() => void command(d, c)}>
                                  {COMMAND_LABEL[c]}
                                </Button>
                              ))}
                              {user?.isAdmin && d.commands.includes('sensors-on') && !sensorsState(d).startsWith('blocked') && (
                                sensorsState(d).startsWith('on') ? (
                                  <Button loading={busy === `${d.id}:sensors-off`} onClick={() => void command(d, 'sensors-off')}>
                                    {COMMAND_LABEL['sensors-off']}
                                  </Button>
                                ) : (
                                  <Button
                                    loading={busy === `${d.id}:sensors-on`}
                                    title="Installs the PawnIO driver on this PC so the CPU's temperature, power and fans can be read"
                                    onClick={() => void command(d, 'sensors-on')}
                                  >
                                    {COMMAND_LABEL['sensors-on']}
                                  </Button>
                                )
                              )}
                              {d.lastCommand && (
                                <span
                                  className="text-ink-secondary"
                                  style={{ color: d.lastCommand.state === 'failed' ? 'var(--color-critical)' : undefined }}
                                >
                                  {COMMAND_LABEL[d.lastCommand.action as Command] ?? d.lastCommand.action} by {d.lastCommand.by},{' '}
                                  {formatRelative(d.lastCommand.at)}:{' '}
                                  {d.lastCommand.state === 'sent' ? 'waiting for the device…' : d.lastCommand.message || d.lastCommand.state}
                                </span>
                              )}
                            </div>
                          )}
                          <DeviceHistory deviceId={d.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          </Card>
        ))
      )}

      {blocked.length > 0 && (
        <Card title="Declined and blocked">
          <div className="divide-y divide-grid">
            {blocked.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-sm">
                <div className="min-w-0">
                  <span className="font-medium">{d.name}</span>
                  <span className="ml-2 text-xs text-ink-muted">
                    {appLabel(d)}{d.lastIp && ` · from ${d.lastIp}`} · last tried {formatRelative(d.lastSeenAt)}
                  </span>
                </div>
                {can('device:manage') && (
                  <div className="flex shrink-0 gap-1">
                    <Button
                      loading={busy === d.id}
                      onClick={() => void act(d.id, () => api.patch(path(d), { blocked: false }), 'Could not unblock it.')}
                    >
                      Unblock
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={busy === d.id}
                      title="Forget it. If it connects again it will ask to join again."
                      onClick={() => void act(d.id, () => api.delete(path(d)), 'Could not forget it.')}
                    >
                      Forget
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {adopting && (
        <AdoptModal
          request={adopting === true ? null : adopting}
          groups={groups}
          onClose={() => setAdopting(null)}
          onDone={() => { setAdopting(null); void load(); }}
        />
      )}

      {editing && (
        <EditModal
          device={editing}
          groups={groups}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); void load(); }}
        />
      )}

      <Modal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.name ?? ''}`}
        footer={
          <>
            <Button onClick={() => setRemoving(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={busy === removing?.id}
              onClick={() => removing && void act(removing.id, () => api.delete(path(removing)), 'Could not remove it.')}
            >
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-secondary">
          Its page stops loading for everyone straight away, and the device goes back to waiting to be adopted. Nothing
          on the device itself is changed - it keeps running. To have it back, accept it again with its claim code.
        </p>
      </Modal>

      <Modal
        open={!!declining}
        onClose={() => setDeclining(null)}
        title={`Decline ${declining?.name ?? ''}`}
        footer={
          <>
            <Button onClick={() => setDeclining(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={busy === declining?.id}
              onClick={() => declining && void act(declining.id, () => api.post(`${path(declining)}/decline`), 'Could not decline it.')}
            >
              Decline and block
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-secondary">
          The device is blocked: it is refused each time it tries, and stops asking. It stays under "Declined and
          blocked", where it can be unblocked.
        </p>
      </Modal>
    </div>
  );
}

function Details({ device: d }: { device: Device }) {
  const rows: [string, string][] = [
    ...(d.status?.fields ?? []).map((f) => [f.label, f.value] as [string, string]),
  ];
  return (
    <div className="grid gap-4 text-xs md:grid-cols-2">
      <div>
        <div className="mb-1 font-medium text-ink-secondary">
          Reported by the device{!d.online && d.status ? ' (when last connected)' : ''}
        </div>
        {rows.length === 0 ? (
          <div className="text-ink-muted">Nothing reported.</div>
        ) : (
          <table>
            <tbody>
              {rows.map(([label, value], i) => (
                <tr key={i}>
                  <td className="pr-4 align-top text-ink-muted">{label}</td>
                  <td className="break-all text-ink">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <div className="mb-1 font-medium text-ink-secondary">Known to the hub</div>
        <table>
          <tbody>
            <tr><td className="pr-4 text-ink-muted">Client ID</td><td className="font-mono text-ink">{d.clientId}</td></tr>
            <tr><td className="pr-4 text-ink-muted">Device id</td><td className="break-all font-mono text-ink">{d.id}</td></tr>
            <tr><td className="pr-4 text-ink-muted">Connects from</td><td className="text-ink">{d.lastIp ?? '—'}</td></tr>
            <tr>
              <td className="pr-4 text-ink-muted">Adopted</td>
              <td className="text-ink">{formatRelative(d.adoptedAt)}{d.adoptedBy && ` by ${d.adoptedBy}`}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GroupSelect({ groups, value, onChange }: { groups: ResourceGroup[]; value: string; onChange: (v: string) => void }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">No group</option>
      {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
    </Select>
  );
}

function AdoptModal({ request, groups, onClose, onDone }: {
  request: Device | null;
  groups: ResourceGroup[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [claimCode, setClaimCode] = useState('');
  const [name, setName] = useState(request?.name ?? '');
  const [groupId, setGroupId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post('/devices/adopt', {
        claimCode, name, groupId: groupId || null, ...(request ? { deviceId: request.id } : {}),
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not adopt the device.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={request ? `Accept ${request.name}` : 'Adopt a device'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!claimCode.trim() || !name.trim()} onClick={() => void submit()}>
            {request ? 'Accept' : 'Adopt'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <Alert tone="critical">{error}</Alert>}
        <Field
          label="Claim code"
          hint="Shown on the device, in its remote access settings. Capitals and the dash do not matter."
        >
          <Input
            autoFocus
            value={claimCode}
            onChange={(e) => setClaimCode(e.target.value)}
            placeholder="K7QM-2XHD"
            className="font-mono uppercase"
            autoComplete="off"
          />
        </Field>
        <Field label="Name" hint="What the hub calls it. The device is told its name.">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Venue 1 ATEM Sync" />
        </Field>
        <Field label="Group" hint="Who can see and open it: people in this group, and administrators.">
          <GroupSelect groups={groups} value={groupId} onChange={setGroupId} />
        </Field>
      </div>
    </Modal>
  );
}

function EditModal({ device, groups, onClose, onDone }: {
  device: Device;
  groups: ResourceGroup[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(device.name);
  const [groupId, setGroupId] = useState(device.groupId ?? '');
  const [busy, setBusy] = useState<'save' | 'block' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(kind: 'save' | 'block', body: Record<string, unknown>): Promise<void> {
    setBusy(kind);
    setError(null);
    try {
      await api.patch(`/devices/${encodeURIComponent(device.id)}`, body);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${device.name}`}
      footer={
        <>
          <Button
            variant="ghost"
            className="mr-auto"
            loading={busy === 'block'}
            title="Cut it off and refuse it until it is unblocked"
            onClick={() => void send('block', { blocked: true })}
          >
            Block
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy === 'save'}
            disabled={!name.trim()}
            onClick={() => void send('save', { name, groupId: groupId || null })}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <Alert tone="critical">{error}</Alert>}
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        </Field>
        <Field label="Group" hint="Who can see and open it: people in this group, and administrators.">
          <GroupSelect groups={groups} value={groupId} onChange={setGroupId} />
        </Field>
      </div>
    </Modal>
  );
}
